import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock, Mail, Phone } from 'lucide-react';

const CONTACT_CARDS = [
  {
    icon: Mail,
    title: 'Email Us',
    lines: [{ label: 'woollsjack@cemeteryburialsolutions.org', href: 'mailto:woollsjack@cemeteryburialsolutions.org', break: true }],
  },
  {
    icon: Phone,
    title: 'Call Us',
    lines: [
      { label: '(800) 370-0969', href: 'tel:8003700969' },
      { label: '(443) 771-0498', href: 'tel:4437710498' },
    ],
  },
  {
    icon: Clock,
    title: 'Hours',
    lines: [{ label: 'Available 24 hours, 7 days a week' }],
  },
];

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);

  /**
   * NOTE: there is still no backend behind this form — submitting used to
   * console.log and nothing else, so the button looked broken. It now
   * acknowledges the visitor, but the message is NOT delivered anywhere yet.
   * Wire this to Netlify Forms (or an email service) before launch.
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-slate-50">
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy-gradient py-20 text-center text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(55% 70% at 50% 0%, rgba(78,143,233,0.35) 0%, rgba(8,45,104,0) 70%)',
          }}
        />
        <div className="container relative mx-auto px-4">
          <p className="eyebrow animate-fade-up text-navy-200">We&rsquo;re here for you</p>
          <h1 className="mt-3 animate-fade-up text-4xl text-white md:text-5xl">Contact Us</h1>
          <p className="mx-auto mt-4 max-w-2xl animate-fade-up font-montserrat leading-relaxed text-navy-100">
            We&rsquo;re here to help and answer any questions you might have.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="mx-auto max-w-6xl">
          {/* Canonical contact listing. */}
          <div className="mb-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {CONTACT_CARDS.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-navy-200 hover:shadow-card-hover"
                >
                  <span className="icon-chip">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h2 className="mt-5 text-xl">{card.title}</h2>
                  <div className="mt-2 space-y-0.5">
                    {card.lines.map((line) =>
                      line.href ? (
                        <a
                          key={line.label}
                          href={line.href}
                          className={`block font-montserrat text-sm text-slate-600 transition-colors hover:text-navy-800 ${
                            line.break ? 'break-all' : ''
                          }`}
                        >
                          {line.label}
                        </a>
                      ) : (
                        <p key={line.label} className="font-montserrat text-sm text-slate-600">
                          {line.label}
                        </p>
                      ),
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {/* Contact Form */}
            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-card">
              <h2 className="text-2xl">Send Us a Message</h2>
              <div className="rule mt-3 !mx-0 !w-12 bg-gradient-to-r from-navy-400 to-transparent" />

              {sent ? (
                <div className="flex flex-col items-center py-10 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-navy-50 ring-1 ring-inset ring-navy-100">
                    <CheckCircle2 className="h-9 w-9 text-navy-700" />
                  </span>
                  <h3 className="mt-5 text-2xl">Thank you, {formData.name || 'friend'}</h3>
                  <p className="mt-3 max-w-sm font-montserrat text-sm leading-relaxed text-slate-600">
                    Your message has been received. A member of our team will be in touch within
                    one business day.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSent(false);
                      setFormData({ name: '', email: '', phone: '', message: '' });
                    }}
                    className="mt-6 font-montserrat text-sm font-medium text-navy-700 underline underline-offset-4 hover:text-navy-900"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                  <div>
                    <label htmlFor="name" className="field-label">
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="input"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="field-label">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="field-label">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="field-label">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className="input"
                      required
                    ></textarea>
                  </div>

                  <button type="submit" className="btn-primary w-full">
                    Send Message
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              )}
            </div>

            {/* Supporting panels. */}
            <div className="flex flex-col gap-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-card">
                <h2 className="text-2xl">How We Can Help</h2>
                <p className="mt-4 font-montserrat text-sm leading-relaxed text-slate-600">
                  For questions about our services, or to discuss how we can assist you, please
                  don&rsquo;t hesitate to reach out. Our team is dedicated to providing
                  compassionate and professional support.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    'Reserving a burial or cremation site',
                    'Pre-planning and payment options',
                    'Cemetery development and records digitization',
                    'Partnership and vendor enquiries',
                  ].map((item) => (
                    <li key={item} className="flex gap-2.5 font-montserrat text-sm text-slate-600">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-navy-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-navy-200 bg-navy-50 p-8">
                <h2 className="text-2xl">Looking for a specific site?</h2>
                <p className="mt-3 font-montserrat text-sm leading-relaxed text-slate-600">
                  You can browse the cemeteries we manage and view live availability on an
                  interactive site map — no appointment needed.
                </p>
                <Link to="/cemeteries" className="btn-primary mt-5">
                  Find a cemetery
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
